import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Zap, Sparkles, Sunrise, ArrowRight } from "lucide-react";
import { CompassLogo } from "@/components/CompassLogo";
import { LanguageToggle } from "@/components/LanguageToggle";
import { Screen } from "@/components/Screen";
import { useI18n } from "@/i18n/I18nProvider";
import { markOnboardingCompleted } from "@/store/store";

const POINT_ICONS = [Zap, Sparkles, Sunrise];

export default function Onboarding() {
  const { t, tArray } = useI18n();
  const navigate = useNavigate();
  const points = tArray("onboarding.points");

  const handleStart = () => {
    markOnboardingCompleted();
    navigate("/today", { replace: true });
  };

  return (
    <div className="relative min-h-[100dvh]" data-testid="onboarding-screen">
      <LanguageToggle variant="top-right" />
      <Screen withBottomNav={false}>
        <div className="flex flex-col items-center text-center pt-6">
          <CompassLogo size={132} />

          <p
            className="mt-8 text-xs uppercase tracking-[0.22em] text-[#E8D8B8]/80"
            data-testid="onboarding-tagline"
          >
            {t("app.tagline")}
          </p>
          <h1
            className="mt-3 text-4xl sm:text-5xl font-medium leading-tight"
            style={{ letterSpacing: "-0.025em" }}
            data-testid="onboarding-title"
          >
            {t("onboarding.title")}
          </h1>
          <p
            className="mt-4 text-base sm:text-lg text-[#E8D8B8]"
            style={{ letterSpacing: "-0.005em" }}
          >
            {t("onboarding.subtitle")}
          </p>

          <p className="mt-5 text-[15px] leading-relaxed text-[#CBD5E1] max-w-[380px]">
            {t("onboarding.description")}
          </p>
        </div>

        <ul
          className="mt-10 space-y-3"
          data-testid="onboarding-points"
        >
          {points.map((point, i) => {
            const Icon = POINT_ICONS[i] || Sparkles;
            return (
              <li
                key={i}
                className="dc-card flex items-center gap-3 px-4 py-3"
              >
                <span
                  className="flex-none w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(244,201,93,0.16), rgba(167,139,250,0.16))",
                  }}
                >
                  <Icon
                    size={18}
                    className="text-[#F4C95D]"
                    strokeWidth={1.6}
                  />
                </span>
                <span className="text-[15px] text-[#F8FAFC]/90">{point}</span>
              </li>
            );
          })}
        </ul>

        <div className="mt-10">
          <button
            type="button"
            onClick={handleStart}
            data-testid="onboarding-cta"
            className="group w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-[15px] font-medium transition-all duration-300 shadow-[0_10px_30px_-15px_rgba(244,201,93,0.6)] hover:shadow-[0_14px_36px_-14px_rgba(244,201,93,0.8)] active:scale-[0.985]"
            style={{
              background:
                "linear-gradient(180deg, #F5D07A 0%, #F4C95D 100%)",
              color: "#0F172A",
              letterSpacing: "-0.005em",
            }}
          >
            {t("onboarding.cta")}
            <ArrowRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-0.5"
              strokeWidth={2}
            />
          </button>

          <p
            className="mt-4 text-center text-[13px] text-[#94A3B8] leading-relaxed max-w-[360px] mx-auto"
            data-testid="onboarding-guest-note"
          >
            {t("onboarding.guestNote")}
          </p>

          <div className="mt-6 flex justify-center">
            <Link
              to="/settings"
              data-testid="onboarding-settings-link"
              className="text-sm text-[#CBD5E1] hover:text-white transition-colors duration-200 underline underline-offset-4 decoration-white/20 hover:decoration-white/40"
            >
              {t("onboarding.settingsLink")}
            </Link>
          </div>
        </div>
      </Screen>
    </div>
  );
}
